import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ButtonLoadingContent, loadingButtonClass } from "@/components/buttons/ButtonLoadingContent";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import AddGroupsModal from "@/components/modals/AddGroupsModal";
import AddRolesModal from "@/components/modals/AddRolesModal";
import { postInvitation } from "@/services/user.service";
import { BUTTONS, GLOBAL_ORGANIZATION, HEADINGS, LABELS } from "@/utils/constants/appConstants/commonConstants";
import { BULK_USER_INVITE_SUCCESS, INVALID_EMAIL_ADDRESS, INVALID_INVITE_EXIST, INVITATION_RESEND_SUCCESSFULLY } from "@/utils/constants/messages";
import { isDisposableEmail, isEmail, validateEmail } from "@/utils/functions/helperFunctions";
import { successToasterMessage } from "@/utils/functions/toaster";
import { AddADUserModal } from "@/components/modals/AddADUserModal";
import { getCurrentOrganization } from "@/utils/functions/OrganizationFunctions";
import { fetchConfig } from "@/services/setting.service";
import { CONNECTOR_TYPES } from "@/utils/constants/appConstants/DirectoryConstants";
import { cn } from "@/lib/utils";
import { Input } from "@/components/ui/input";

type InviteMetaRowProps = {
    label: string;
    labelId: string;
    count: number;
    countId: string;
    buttonId: string;
    buttonText: string;
    onAdd: () => void;
};

function InviteMetaRow({
    label,
    labelId,
    count,
    countId,
    buttonId,
    buttonText,
    onAdd,
}: InviteMetaRowProps) {
    return (
        <div className="mb-4">
            <ul className="gs-invite-meta-row">
                <li>
                    <span className="text-sm text-card-foreground" id={labelId}>
                        {label}
                    </span>
                </li>
                <li>
                    <span className="text-sm text-card-foreground" id={countId}>
                        {count}
                    </span>
                </li>
                <li>
                    <Button type="button" id={buttonId} onClick={onAdd}>
                        {buttonText}
                    </Button>
                </li>
            </ul>
        </div>
    );
}

const InviteUser = () => {
    const [email_address, setEmail_address] = useState("");
    const [userRoles, setUserRoles] = useState<any>([]);
    const [userGroups, setUserGroups] = useState<any>([]);
    const [bulkUsersMsg, setBulkUsersMsg] = useState("");
    const [userErrMsg, setUserErrMsg] = useState("");
    const [userMsg, setUserMsg] = useState("");
    const [showRoleModal, setShowRoleModal] = useState(false);
    const [showGroupModal, setShowGroupModal] = useState(false);
    const [bulkCountSent, setBulkCountSent] = useState(0);
    const [bulkCountAlreadyInvited, setBulkCountAlreadyInvited] = useState(0);
    const [bulkCountResentInvitation, setBulkCountResentInvitation] = useState(0);
    const [bulkCountAlreadyRegistered, setBulkCountAlreadyRegistered] = useState(0);
    const [bulkUsers, setBulkUsers] = useState("");
    const [isSingleInviteLoading, setIsSingleInviteLoading] = useState(false);
    const [isBulkInviteLoading, setIsBulkInviteLoading] = useState(false);
    const [invalidEmails, setInvalidEmails] = useState(0);
    const [disposableEmails, setDisposableEmails] = useState(0);
    const [totalEmails, setTotalEmails] = useState(0);
    const [isBulkInviteErr, setIsBulkInviteErr] = useState(false);
    const [showAddADUserModal, setShowAddADUserModal] = useState(false);
    const [tab, setTab] = useState("single");
    const [ADEnabled, setADEnabled] = useState(false);
    const navigate = useNavigate();
    const current_organization = getCurrentOrganization();
    const [activeTab, setActiveTab] = useState<"single" | "bulk">("single");
    const bulkUsersRef = useRef<HTMLTextAreaElement>(null);

    const focusTextarea = () => {
        bulkUsersRef.current?.focus();
    };

    useEffect(() => {
        fetchConfig(
            null,
            (res: any) => {
                if (res?.directory_connector_type === CONNECTOR_TYPES.OIDC) {
                    setADEnabled(res.is_directory_service_enabled);
                }
            },
            (err: any) => {
                console.log(err);
            }
        );
    }, []);

    useEffect(() => {
        if (activeTab === "bulk") {
            const timer = setTimeout(focusTextarea, 100);
            return () => clearTimeout(timer);
        }
    }, [activeTab]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setUserErrMsg("");
        setUserMsg("");
        setEmail_address(e.target.value);
    };

    const handleInvite = () => {
        if (!isEmail(email_address)) {
            setUserErrMsg(INVALID_EMAIL_ADDRESS);
            return;
        }
        setIsSingleInviteLoading(true);
        const status_message_map: Record<string, string> = {
            "000": "Invitation sent successfully",
            "004": "Invitation resent successfully",
            "001": "User already invited",
            "002": "User already registered",
        };
        const data = {
            invitations: [
                {
                    email_address,
                    roles: userRoles.map((role: any) => role?.id),
                    groups: userGroups.map((group: any) => group?.id),
                },
            ],
        };
        postInvitation(
            data,
            (response: any) => {
                setIsSingleInviteLoading(false);
                if (response.status === 201) {
                    if (
                        response.data.invitations[0].status_code === "000" ||
                        response.data.invitations[0].status_code === "004"
                    ) {
                        successToasterMessage(
                            status_message_map[response.data.invitations[0].status_code]
                        );
                        setUserMsg(status_message_map[response.data.invitations[0].status_code]);
                        setUserGroups([]);
                        setUserRoles([]);
                        setEmail_address("");
                        setUserErrMsg("");
                    } else {
                        setUserErrMsg(status_message_map[response.data.invitations[0].status_code]);
                    }
                }
            },
            (error: any) => {
                console.log(error);
                setIsSingleInviteLoading(false);
                setUserErrMsg(error?.message);
                setUserMsg("");
            }
        );
    };

    const handleinvitebulkusers = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setBulkUsersMsg("");
        setBulkUsers(e.target.value);
    };

    const handleUsersSelected = (emails: any) => {
        if (tab === "single") {
            setEmail_address(emails[0].email_address);
        } else {
            const userEmails = emails
                .map((emailObject: any) => emailObject.email_address)
                .join("\n");
            setBulkUsers(userEmails);
        }
        setShowAddADUserModal(false);
    };

    let invalidEmailsArr: string[];
    let disposableEmailArr: string[];

    const invitebulkusers = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setIsBulkInviteLoading(true);
        invalidEmailsArr = bulkUsers
            .trim()
            .replace(/\s*\n\s*/g, "\n")
            .split("\n")
            .filter((user) => !validateEmail(user));
        disposableEmailArr = bulkUsers
            .trim()
            .replace(/\s*\n\s*/g, "\n")
            .split("\n")
            .filter((user) => isDisposableEmail(user));
        setInvalidEmails(invalidEmailsArr.length);
        setDisposableEmails(disposableEmailArr.length);
        const emails = bulkUsers
            .trim()
            .replace(/\s*\n\s*/g, "\n")
            .split("\n")
            .filter((a) => !isDisposableEmail(a) && validateEmail(a));
        setTotalEmails(
            bulkUsers.trim().replace(/\s*\n\s*/g, "\n").split("\n").length
        );
        const payload = {
            invitations: emails.map((email: string) => ({
                email_address: email,
                roles: userRoles.map((role: any) => role.id),
                groups: userGroups.map((group: any) => group.id),
            })),
        };
        postInvitation(
            payload,
            (response: any) => {
                setIsBulkInviteLoading(false);
                if (response.status === 201) {
                    const sent = response.data.invitations.filter(
                        (user: { status_code: string }) => user.status_code === "000"
                    ).length;
                    const alreadyRegistered = response.data.invitations.filter(
                        (user: { status_code: string }) => user.status_code === "002"
                    ).length;
                    const alreadyInvited = response.data.invitations.filter(
                        (user: { status_code: string }) => user.status_code === "001"
                    ).length;
                    const resentInvitation = response.data.invitations.filter(
                        (user: { status_code: string }) => user.status_code === "004"
                    ).length;
                    setBulkCountSent(sent);
                    setBulkCountAlreadyRegistered(alreadyRegistered);
                    setBulkCountAlreadyInvited(alreadyInvited);
                    setBulkCountResentInvitation(resentInvitation);
                    if (sent > 0) {
                        successToasterMessage(BULK_USER_INVITE_SUCCESS);
                    }
                    if (resentInvitation > 0) {
                        successToasterMessage(INVITATION_RESEND_SUCCESSFULLY);
                    }
                    setUserGroups([]);
                    setUserRoles([]);
                    setBulkUsers("");
                    setIsBulkInviteErr(true);
                    setBulkUsers(`${invalidEmailsArr.join("\n")}\n${disposableEmailArr.join("\n")}`);
                } else {
                    setBulkUsersMsg(INVALID_INVITE_EXIST);
                }
            },
            (error: any) => {
                setIsBulkInviteLoading(false);
                setBulkUsersMsg(error?.message);
            }
        );
    };

    const showImportFromAd =
        current_organization.name !== GLOBAL_ORGANIZATION && ADEnabled;

    const inviteActions = (
        idPrefix: "single" | "bulk",
        inviteLoading: boolean,
        onInvite?: () => void,
        submitType: "button" | "submit" = "button",
        inviteDisabled = false
    ) => (
        <ul className="gs-invite-actions">
            <li>
                <Button
                    id={`invite-${idPrefix}-user-tab-invite-btn`}
                    type={submitType}
                    className={loadingButtonClass(inviteLoading)}
                    disabled={inviteDisabled || inviteLoading}
                    aria-busy={inviteLoading || undefined}
                    onClick={onInvite}
                >
                    <ButtonLoadingContent
                        loading={inviteLoading}
                        loaderId={`invite-${idPrefix}-user-tab-invite-btn-loader`}
                    >
                        {BUTTONS.INVITE}
                    </ButtonLoadingContent>
                </Button>
            </li>
            <li>
                <Button
                    id={`invite-${idPrefix}-user-tab-cancel-btn`}
                    type="button"
                    variant="light"
                    onClick={() => navigate("/dashboard")}
                >
                    {BUTTONS.CANCEL}
                </Button>
            </li>
        </ul>
    );

    return (
        <>
            <div className="gs-invite-page">
                <div className="gs-invite-page__header">
                    <h4 className="title gs-invite-page__title" id="invite-user-title">
                        {HEADINGS.INVITE_USERS}
                    </h4>
                </div>
                <div className="gs-invite-page__body">
                    <ul className="gs-page-tabs">
                        <li>
                            <button
                                type="button"
                                id="invite-user-single-user-tab"
                                className={cn(
                                    "gs-page-tab",
                                    activeTab === "single" && "gs-page-tab--active"
                                )}
                                onClick={() => setActiveTab("single")}
                            >
                                {HEADINGS.SINGLE_USER}
                            </button>
                        </li>
                        <li>
                            <button
                                type="button"
                                id="invite-user-bulk-user-tab"
                                className={cn(
                                    "gs-page-tab",
                                    activeTab === "bulk" && "gs-page-tab--active"
                                )}
                                onClick={() => setActiveTab("bulk")}
                            >
                                {HEADINGS.BULK_USERS}
                            </button>
                        </li>
                    </ul>

                    {activeTab === "single" && (
                        <div className="gs-invite-tab-panel" id="tabItem1">
                            {showImportFromAd && (
                                <p className="mb-4">
                                    <Button
                                        type="button"
                                        id="invite-bulk-user-tab-import-from-ad-btn"
                                        onClick={() => {
                                            setShowAddADUserModal(true);
                                            setTab("single");
                                        }}
                                    >
                                        {BUTTONS.IMPORT_FROM_AD}
                                    </Button>
                                </p>
                            )}
                            <div className="gs-invite-form-col">
                                <div className="mb-4">
                                    <label
                                        className="gs-form-label"
                                        htmlFor="invite-single-user-tab-email-address-input"
                                        id="invite-single-user-tab-email-address-label"
                                    >
                                        {LABELS.EMAIL_ADDRESS}
                                    </label>
                                    <Input
                                        id="invite-single-user-tab-email-address-input"
                                        type="email"
                                        placeholder="Enter email"
                                        required
                                        value={email_address}
                                        onChange={handleChange}
                                        className="h-[calc(2.625rem+2px)] text-[0.9375rem]"
                                    />
                                </div>
                                <InviteMetaRow
                                    label={HEADINGS.ROLES}
                                    labelId="invite-single-user-tab-role-label"
                                    count={userRoles?.length}
                                    countId="invite-single-user-tab-role-count"
                                    buttonId="invite-single-user-tab-add-role-btn"
                                    buttonText={BUTTONS.ADD_ROLES}
                                    onAdd={() => setShowRoleModal(true)}
                                />
                                <InviteMetaRow
                                    label={HEADINGS.GROUPS}
                                    labelId="invite-single-user-tab-group-label"
                                    count={userGroups?.length}
                                    countId="invite-single-user-tab-group-count"
                                    buttonId="invite-single-user-tab-add-group-btn"
                                    buttonText={BUTTONS.ADD_GROUPS}
                                    onAdd={() => setShowGroupModal(true)}
                                />
                            </div>
                            {inviteActions(
                                "single",
                                isSingleInviteLoading,
                                handleInvite,
                                "button",
                                !email_address || userGroups.length + userRoles.length === 0
                            )}
                            {userErrMsg && (
                                <p
                                    id="invite-single-user-tab-error-msg"
                                    className="px-2 text-danger"
                                >
                                    {userErrMsg}
                                </p>
                            )}
                            {userMsg && (
                                <p
                                    id="invite-single-user-tab-success-msg"
                                    className="px-2 text-success"
                                >
                                    {userMsg}
                                </p>
                            )}
                        </div>
                    )}

                    {activeTab === "bulk" && (
                        <div className="gs-invite-tab-panel" id="tabItem2">
                            {showImportFromAd && (
                                <p className="mb-4">
                                    <Button
                                        type="button"
                                        id="invite-bulk-user-tab-import-from-ad-btn"
                                        onClick={() => {
                                            setShowAddADUserModal(true);
                                            setTab("bulk");
                                        }}
                                    >
                                        {BUTTONS.IMPORT_FROM_AD}
                                    </Button>
                                </p>
                            )}
                            <form id="usrform" onSubmit={invitebulkusers}>
                                <div className="gs-invite-form-col">
                                    <div className="mb-4">
                                        <label
                                            className="gs-form-label"
                                            htmlFor="invite-bulk-user-tab-email-input"
                                            id="invite-bulk-user-tab-email-label"
                                        >
                                            {LABELS.LINE_SEPERATED_EMAILS}
                                        </label>
                                        <Textarea
                                            ref={bulkUsersRef}
                                            onChange={handleinvitebulkusers}
                                            id="invite-bulk-user-tab-email-input"
                                            rows={4}
                                            name="content"
                                            className="mb-0 min-h-[7.5rem] text-[0.9375rem]"
                                            form="usrform"
                                            value={bulkUsers}
                                        />
                                    </div>
                                    <InviteMetaRow
                                        label={HEADINGS.ROLES}
                                        labelId="invite-bulk-user-tab-role-label"
                                        count={userRoles?.length}
                                        countId="invite-bulk-user-tab-role-count"
                                        buttonId="invite-bulk-user-tab-add-role-btn"
                                        buttonText={BUTTONS.ADD_ROLES}
                                        onAdd={() => setShowRoleModal(true)}
                                    />
                                    <InviteMetaRow
                                        label={HEADINGS.GROUPS}
                                        labelId="invite-bulk-user-tab-group-label"
                                        count={userGroups?.length}
                                        countId="invite-bulk-user-tab-group-count"
                                        buttonId="invite-bulk-user-tab-add-group-btn"
                                        buttonText={BUTTONS.ADD_GROUPS}
                                        onAdd={() => setShowGroupModal(true)}
                                    />
                                </div>
                                {inviteActions(
                                    "bulk",
                                    isBulkInviteLoading,
                                    undefined,
                                    "submit",
                                    userRoles?.length + userGroups?.length === 0 || bulkUsers === ""
                                )}
                            </form>
                            <div>
                                {isBulkInviteErr && (
                                    <>
                                        <p
                                            id="invite-bulk-user-tab-invited-error-msg"
                                            className="text-danger"
                                        >
                                            {bulkCountSent}/{totalEmails} user invited successfully.
                                        </p>
                                        <p
                                            id="invite-bulk-user-tab-already-registered-error-msg"
                                            className="text-danger"
                                        >
                                            {bulkCountAlreadyRegistered}/{totalEmails} users already
                                            registered.
                                        </p>
                                        <p
                                            id="invite-bulk-user-tab-already-invited-error-msg"
                                            className="text-danger"
                                        >
                                            {bulkCountResentInvitation}/{totalEmails} user re-invited
                                            successfully.
                                        </p>
                                        <p
                                            id="invite-bulk-user-tab-invalid-email-error-msg"
                                            className="text-danger"
                                        >
                                            {invalidEmails}/{totalEmails} emails are invalid.
                                        </p>
                                        <p
                                            id="invite-bulk-user-tab-disposable-error-msg"
                                            className="text-danger"
                                        >
                                            {disposableEmails}/{totalEmails} emails are disposable.
                                        </p>
                                    </>
                                )}
                            </div>
                            {!isBulkInviteErr && (
                                <p className="text-danger">{bulkUsersMsg}</p>
                            )}
                        </div>
                    )}
                </div>
            </div>
            <AddGroupsModal
                show={showGroupModal}
                onHide={() => setShowGroupModal(false)}
                usergroups={userGroups}
                setUserGroups={setUserGroups}
            />
            <AddRolesModal
                show={showRoleModal}
                onHide={() => setShowRoleModal(false)}
                userroles={userRoles}
                setUserRoles={setUserRoles}
            />
            <AddADUserModal
                tab={tab}
                show={showAddADUserModal}
                onHide={() => setShowAddADUserModal(false)}
                onUsersSelected={handleUsersSelected}
            />
        </>
    );
};

export default InviteUser;
